import styled from 'styled-components';
import { Col, Button, Select, Slider } from 'antd';

import { ArrowSlimIcon } from '@/assets/icons/v2';
import { timelineTheme } from '@/components/ui/Timeline/components/theme';
import { TimeLabelsContainer, TimeTooltip } from '@/components/ui/Timeline/components/ui/styles';

export const CARD_BACKGROUND = '#F8F8F8';
const CARD_RADIUS = '1rem';
const STRETCH_BAR_HEIGHT = `${timelineTheme.dimensions.cycleBarHeight}px`;
const DELETE_COLOR = '#E74150';
const SCROLLBAR_SIZE = '0.375rem';
const SLIDER_TRACK_HEIGHT = '0.25rem';
const SLIDER_HANDLE_SIZE = '1rem';
const ZOOM_ICON_SIZE = '1rem';
const ACTION_RADIUS = '0.25rem';
const FIELD_BORDER_RADIUS = '0.25rem';
const FIELD_HEIGHT = '2.5rem';
const TIME_ICON_SIZE = '1rem';
const FIELD_TEXT_SIZE = '0.875rem';
const FIELD_LINE_HEIGHT = '1.375rem';
const FIELD_INNER_HEIGHT = 'calc(2.5rem - 2px)';
const TIMEPICKER_WIDTH = '6.6875rem';
const ACTION_SIZE = '2.5rem';
const REMOVE_ACTION_SIZE = '2rem';
const RULER_MARGIN = '0.75rem';

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2rem;
	padding: 1.5rem;
	background: ${({ theme }) => theme.palette.secondary[50]};
	border-radius: ${CARD_RADIUS};
`;

export const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

export const MarkStretchButton = styled(Button)`
	height: 2.5rem;
	padding: 0 0.75rem;
	border-radius: 0.25rem;

	&&:disabled {
		background: ${({ theme }) => theme.palette.secondary[50]};
		border: none;
		color: ${({ theme }) => theme.palette.secondary[250]};
	}
`;

export const TimelineViewport = styled.div`
	width: 100%;
	padding-top: 1.25rem;
	overflow-x: scroll;
	overflow-y: hidden;

	&::-webkit-scrollbar {
		height: ${SCROLLBAR_SIZE};
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: ${({ theme }) => theme.palette.secondary[250]};
		border-radius: ${SCROLLBAR_SIZE};
	}

	${TimeLabelsContainer},
	${TimeTooltip} {
		display: none;
	}
`;

interface TimelineTrackProps {
	widthInPercent: number;
}

export const TimelineTrack = styled.div.withConfig<TimelineTrackProps>({
	shouldForwardProp: (prop) => prop !== 'widthInPercent'
})`
	position: relative;
	width: ${({ widthInPercent }) => widthInPercent}%;
	min-width: 100%;
`;

interface VideoEndMarkerProps {
	ratio: number;
}

export const VideoEndMarker = styled.div.withConfig<VideoEndMarkerProps>({
	shouldForwardProp: (prop) => prop !== 'ratio'
})`
	position: absolute;
	top: 0;
	bottom: 0;
	left: calc(${RULER_MARGIN} + ${({ ratio }) => ratio} * (100% - ${RULER_MARGIN} * 2));
	border-left: 1px dashed ${({ theme }) => theme.palette.primary[800]};
	pointer-events: none;
`;

interface StretchAnchorProps {
	startRatio: number;
	widthRatio: number;
}

export const StretchAnchor = styled.div.withConfig<StretchAnchorProps>({
	shouldForwardProp: (prop) => prop !== 'startRatio' && prop !== 'widthRatio'
})`
	position: absolute;
	bottom: 0;
	height: ${STRETCH_BAR_HEIGHT};
	left: calc(${RULER_MARGIN} + ${({ startRatio }) => startRatio} * (100% - ${RULER_MARGIN} * 2));
	width: calc(${({ widthRatio }) => widthRatio} * (100% - ${RULER_MARGIN} * 2));
	pointer-events: none;
`;

export const VideoEndMarkerLabel = styled.span`
	position: absolute;
	bottom: 100%;
	left: 50%;
	color: ${({ theme }) => theme.palette.primary[800]};
	font-size: 0.75rem;
	font-weight: 600;
	line-height: 1rem;
	white-space: nowrap;
	transform: translateX(-50%);
`;

export const RulerLabels = styled.div`
	position: sticky;
	left: 0;
	display: flex;
	justify-content: space-between;
	width: 100%;
	margin-top: 0.5rem;
	color: ${({ theme }) => theme.palette.secondary[500]};
	font-size: 0.875rem;
	line-height: 1rem;
`;

export const ZoomControl = styled(Col)`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	width: 16rem;
`;

export const ZoomSlider = styled(Slider)`
	flex: 1;
	height: ${SLIDER_HANDLE_SIZE};
	margin: 0 calc(${SLIDER_HANDLE_SIZE} / 2);
	padding: 0;

	.ant-slider-rail,
	.ant-slider-track {
		top: 50%;
		height: ${SLIDER_TRACK_HEIGHT};
		border-radius: ${SLIDER_TRACK_HEIGHT};
		transform: translateY(-50%);
	}

	.ant-slider-rail {
		background: ${({ theme }) => theme.palette.secondary[150]};
	}

	.ant-slider-track {
		background: ${({ theme }) => theme.palette.primary[400]};
	}

	.ant-slider-handle {
		top: 50%;
		width: ${SLIDER_HANDLE_SIZE};
		height: ${SLIDER_HANDLE_SIZE};
		margin-top: calc(${SLIDER_HANDLE_SIZE} / -2);
		background: ${({ theme }) => theme.palette.primary[400]};
		border-color: ${({ theme }) => theme.palette.primary[400]};
	}

	&:hover .ant-slider-rail {
		background: ${({ theme }) => theme.palette.secondary[150]};
	}

	&:hover .ant-slider-track {
		background: ${({ theme }) => theme.palette.primary[400]};
	}
`;

export const ZoomButton = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${ZOOM_ICON_SIZE};
	height: ${ZOOM_ICON_SIZE};
	padding: 0;
	border: none;
	box-shadow: none;
	background: transparent;

	svg {
		width: ${ZOOM_ICON_SIZE};
		height: ${ZOOM_ICON_SIZE};
	}
`;

export const Divider = styled.hr`
	width: 100%;
	margin: 0;
	border: none;
	border-top: 1px solid ${({ theme }) => theme.palette.secondary[150]};
`;

export const ListContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const TlidRow = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 1rem;
`;

export const TlidCreationRow = styled.div`
	display: flex;
	flex-wrap: nowrap;
	gap: 0.5rem;
	padding: 0.5rem;
`;

export const TlidSelect = styled(Select)`
	width: 18.375rem;

	&& .ant-select-selector {
		height: ${FIELD_HEIGHT};
		border-color: ${({ theme }) => theme.palette.secondary[150]};
		border-radius: ${FIELD_BORDER_RADIUS};
	}

	&& .ant-select-selector .ant-select-selection-item,
	&& .ant-select-selector .ant-select-selection-placeholder,
	&& .ant-select-selector .ant-select-selection-search-input {
		height: ${FIELD_INNER_HEIGHT};
		line-height: ${FIELD_INNER_HEIGHT};
	}
`;

export const TlidActions = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

export const ActionButton = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${ACTION_SIZE};
	height: ${ACTION_SIZE};
	padding: 0;
	background: ${({ theme }) => theme.palette.secondary.white};
	border: none;
	border-radius: ${ACTION_RADIUS};
	box-shadow: none;

	&:hover,
	&:focus {
		background: ${({ theme }) => theme.palette.secondary.white};
	}
`;

export const ConfirmButton = styled(ActionButton)`
	background: ${({ theme }) => theme.palette.risk.low[100]};
	color: ${({ theme }) => theme.palette.risk.low.default};

	&:hover,
	&:focus {
		background: ${({ theme }) => theme.palette.risk.low[100]};
		color: ${({ theme }) => theme.palette.risk.low.default};
	}
`;

export const DiscardEditButton = styled(ActionButton)`
	border: 1px solid ${({ theme }) => theme.palette.secondary[150]};

	&:hover,
	&:focus {
		border-color: ${({ theme }) => theme.palette.secondary[150]};
	}
`;

export const StretchRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	padding-right: 0.25rem;
`;

export const StretchArrow = styled(ArrowSlimIcon)`
	color: ${({ theme }) => theme.palette.secondary[400]};
	transform: rotate(-90deg);
`;

export const RemoveStretchButton = styled(ActionButton)`
	width: ${REMOVE_ACTION_SIZE};
	height: ${REMOVE_ACTION_SIZE};
	background: transparent;

	&:hover,
	&:focus {
		background: transparent;
	}
`;

export const StretchIndex = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2rem;
	height: 2rem;
	border-radius: 1.25rem;
	background: ${({ theme }) => theme.palette.primary[100]};
	color: ${({ theme }) => theme.palette.secondary[400]};
	font-size: 0.875rem;
	font-weight: 600;
`;

export const StretchInterval = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;

	.ant-picker {
		width: ${TIMEPICKER_WIDTH};
		height: ${FIELD_HEIGHT};
		padding: 0 0.5rem;
		border-color: ${({ theme }) => theme.palette.secondary[150]};
		border-radius: ${FIELD_BORDER_RADIUS};
	}

	.ant-picker-suffix {
		margin-left: 0.5rem;
	}

	.ant-picker-suffix {
		color: ${({ theme }) => theme.palette.secondary[400]};
	}

	.ant-picker-suffix svg {
		width: ${TIME_ICON_SIZE};
		height: ${TIME_ICON_SIZE};
	}
`;

export const StretchDuration = styled.span`
	flex: 1;
	color: ${({ theme }) => theme.palette.secondary[300]};
	font-size: ${FIELD_TEXT_SIZE};
	line-height: ${FIELD_LINE_HEIGHT};
`;

export const FooterRow = styled.div`
	display: flex;
	justify-content: center;
`;

export const AddStretchButton = styled(Button)`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: ${({ theme }) => theme.palette.primary.default};

	&:hover,
	&:focus {
		color: ${({ theme }) => theme.palette.primary.default};
	}
`;

export const DeleteButton = styled(ActionButton)`
	color: ${DELETE_COLOR};

	&:hover,
	&:focus {
		color: ${DELETE_COLOR};
	}
`;

export const RulerSkeleton = styled.div`
	padding: 1rem 0;
`;

export const StretchFileName = styled.span`
	overflow: hidden;
	max-width: 12rem;
	color: ${({ theme }) => theme.palette.secondary[300]};
	font-size: 0.875rem;
	line-height: 1rem;
	text-overflow: ellipsis;
	white-space: nowrap;
`;
